import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  PanelContainer,
  SectionContainer,
  SectionHeader,
  SectionHeaderProfile,
  ToggleIcon,
  SectionTitle,
  SectionTitleProfile,
  SectionContent,
  ProfileCard,
  ProfileCardBlue,
  ProfileCardYellow,
  ProfileRow,
  ProfileRowColumn,
  ProfileLabel,
  ProfileValue,
  ProfileValueSpaced,
  ProfileValueBold,
  ProfileValuePitch,
  ProfileValueContext,
  ParentsContainer,
  ParentBadge,
  MomentCard,
  MomentTimestamp,
  MomentText,
  MomentTextItalic,
  MomentTextItalicWarning,
  MomentLabel,
  MomentLabelGreen,
  MomentLabelRed,
  MomentLabelSpaced,
  SectionWrapper,
  MomentsWrapper,
  QuoteBlock,
  QuoteBlockWhite,
  QuoteText,
  UrgencyBadge,
  ActionCard,
  PriorityBadge,
  PriorityWrapper,
  MessageCard,
  ChannelBadge,
  BadgesWrapper,
  ToneText,
  TimestampButton,
} from './SalesPitchPanel.styles';
import { ExportButton, PanelExportHeader as ExportHeader } from './PanelShared.styles';
import { downloadTextFile } from '../../../utils';

// Utility function to convert timestamp [HH:MM:SS] to seconds
const toSeconds = timestamp => {
  if (!timestamp) return undefined;
  
  // Extract timestamp from brackets if present
  const match = timestamp.match(/\[(\d{2}:\d{2}:\d{2})\]/);
  const timeString = match ? match[1] : timestamp;
  
  const [hh, mm, ss] = timeString.split(':').map(Number);
  return (hh || 0) * 3600 + (mm || 0) * 60 + (ss || 0);
};

// Parse markdown formatting (bold, italic, etc.)
const parseMarkdown = (text) => {
  if (!text) return text;
  
  // Split by markdown patterns while preserving the delimiters
  const boldRegex = /(\*\*[^*]+\*\*)/g;
  const parts = text.split(boldRegex);
  
  return parts.map((part, index) => {
    // Check for bold text
    if (part.startsWith('**') && part.endsWith('**')) {
      const boldText = part.slice(2, -2);
      return <strong key={`bold-${index}`}>{boldText}</strong>;
    }
    return part;
  });
};

// Render text with clickable timestamps and markdown formatting
const renderTextWithTimestamps = (text, onSeek) => {
  if (!text) return null;
  
  const timestampRegex = /\[(\d{2}:\d{2}:\d{2})\]/g;
  const parts = [];
  let lastIndex = 0;
  let match;
  let index = 0;

  while ((match = timestampRegex.exec(text)) !== null) {
    // Add text before timestamp (with markdown parsing)
    if (match.index > lastIndex) {
      const textSegment = text.substring(lastIndex, match.index);
      parts.push(<span key={`text-${index}`}>{parseMarkdown(textSegment)}</span>);
    }

    // Add clickable timestamp
    const timestampSeconds = toSeconds(match[0]);
    parts.push(
      <TimestampButton
        key={`timestamp-${index}`}
        onClick={() => timestampSeconds && onSeek(timestampSeconds)}
        disabled={timestampSeconds === undefined}
      >
        {match[0]}
      </TimestampButton>,
    );

    lastIndex = match.index + match[0].length;
    index++;
  }

  // Add remaining text (with markdown parsing)
  if (lastIndex < text.length) {
    const textSegment = text.substring(lastIndex);
    parts.push(<span key="text-end">{parseMarkdown(textSegment)}</span>);
  }

  return parts.length > 0 ? <span>{parts}</span> : <span>{parseMarkdown(text)}</span>;
};

// Student Profile Section (Always Visible)
const StudentProfileSection = ({ profileData }) => (
  <SectionContainer>
    <SectionHeaderProfile>
      <SectionTitleProfile>Student Profile</SectionTitleProfile>
    </SectionHeaderProfile>
    <SectionContent expanded={true}>
      <ProfileCard>
        <ProfileRow>
          <ProfileLabel>Student Name:</ProfileLabel>
          <ProfileValue>{profileData.name}</ProfileValue>
        </ProfileRow>
        <ProfileRow>
          <ProfileLabel>Grade:</ProfileLabel>
          <ProfileValue>{profileData.grade}</ProfileValue>
        </ProfileRow>
        <ProfileRow>
          <ProfileLabel>Tutor:</ProfileLabel>
          <ProfileValue>{profileData.tutor}</ProfileValue>
        </ProfileRow>
        {profileData.parents && profileData.parents.length > 0 && (
          <ProfileRow>
            <ProfileLabel>Parents:</ProfileLabel>
            <ParentsContainer>
              {profileData.parents.map((parent, index) => (
                <ParentBadge key={index}>
                  {parent.name} ({parent.relation})
                </ParentBadge>
              ))}
            </ParentsContainer>
          </ProfileRow>
        )}
        {profileData.sessionGoal && (
          <ProfileRowColumn>
            <ProfileLabel>Session Goal:</ProfileLabel>
            <ProfileValueSpaced>{profileData.sessionGoal}</ProfileValueSpaced>
          </ProfileRowColumn>
        )}
      </ProfileCard>
    </SectionContent>
  </SectionContainer>
);

StudentProfileSection.propTypes = {
  profileData: PropTypes.object.isRequired,
};

// Session Performance Section
const SessionPerformanceSection = ({ performanceData, onSeek, expanded, onToggle }) => {
  const handleMomentClick = useCallback(
    timestamp => {
      onSeek(toSeconds(timestamp));
    },
    [onSeek],
  );

  return (
    <SectionContainer>
      <SectionHeader onClick={onToggle}>
        <ToggleIcon expanded={expanded} />
        <SectionTitle>Session Performance</SectionTitle>
      </SectionHeader>
      <SectionContent expanded={expanded}>
        {performanceData.whatClicked && performanceData.whatClicked.length > 0 && (
          <MomentsWrapper>
            <MomentLabelGreen>✓ What Clicked</MomentLabelGreen>
            {performanceData.whatClicked.map((moment, index) => (
              <MomentCard key={index} success>
                {moment.timestamp && (
                  <MomentTimestamp onClick={() => handleMomentClick(moment.timestamp)}>
                    {moment.timestamp}
                  </MomentTimestamp>
                )}
                <MomentText>{renderTextWithTimestamps(moment.moment, onSeek)}</MomentText>
                {moment.salesImplication && (
                  <>
                    <MomentLabelSpaced>Sales Implication:</MomentLabelSpaced>
                    <MomentTextItalic>
                      {renderTextWithTimestamps(moment.salesImplication, onSeek)}
                    </MomentTextItalic>
                  </>
                )}
              </MomentCard>
            ))}
          </MomentsWrapper>
        )}

        {performanceData.whatStruggled && performanceData.whatStruggled.length > 0 && (
          <div>
            <MomentLabelRed>✗ What They Struggled With</MomentLabelRed>
            {performanceData.whatStruggled.map((struggle, index) => (
              <MomentCard key={index}>
                {struggle.timestamp && (
                  <MomentTimestamp onClick={() => handleMomentClick(struggle.timestamp)}>
                    {struggle.timestamp}
                  </MomentTimestamp>
                )}
                <MomentText>{renderTextWithTimestamps(struggle.moment, onSeek)}</MomentText>
                {struggle.emotionalImpact && (
                  <>
                    <MomentLabelSpaced>Emotional Impact:</MomentLabelSpaced>
                    <MomentTextItalicWarning>
                      {renderTextWithTimestamps(struggle.emotionalImpact, onSeek)}
                    </MomentTextItalicWarning>
                  </>
                )}
              </MomentCard>
            ))}
          </div>
        )}
      </SectionContent>
    </SectionContainer>
  );
};

SessionPerformanceSection.propTypes = {
  performanceData: PropTypes.object.isRequired,
  onSeek: PropTypes.func.isRequired,
  expanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
};

// Parent Sentiment Section
const ParentSentimentSection = ({ sentimentData, onSeek, expanded, onToggle }) => {
  const handleTimestampClick = useCallback(
    timestamp => {
      onSeek(toSeconds(timestamp));
    },
    [onSeek],
  );

  return (
    <SectionContainer>
      <SectionHeader onClick={onToggle}>
        <ToggleIcon expanded={expanded} />
        <SectionTitle>Parent Sentiment</SectionTitle>
      </SectionHeader>
      <SectionContent expanded={expanded}>
        {sentimentData.decisionMaker && (
          <ProfileCard $marginBottom="16px">
            <ProfileRow>
              <ProfileLabel>Decision Maker:</ProfileLabel>
              <ProfileValue>{sentimentData.decisionMaker.name}</ProfileValue>
            </ProfileRow>
            {sentimentData.decisionMaker.primaryConcern && (
              <ProfileRowColumn>
                <ProfileLabel>Primary Concern:</ProfileLabel>
                <ProfileValueSpaced>
                  {sentimentData.decisionMaker.primaryConcern}
                </ProfileValueSpaced>
              </ProfileRowColumn>
            )}
          </ProfileCard>
        )}

        {sentimentData.urgency && (
          <SectionWrapper>
            <MomentLabel>Urgency Level:</MomentLabel>
            <UrgencyBadge level={sentimentData.urgency.level}>
              {sentimentData.urgency.level}
            </UrgencyBadge>
            {sentimentData.urgency.evidence && (
              <QuoteBlock>
                <QuoteText>"{sentimentData.urgency.evidence.quote}"</QuoteText>
                {sentimentData.urgency.evidence.timestamp && (
                  <TimestampButton
                    onClick={() => handleTimestampClick(sentimentData.urgency.evidence.timestamp)}
                  >
                    {sentimentData.urgency.evidence.timestamp}
                  </TimestampButton>
                )}
              </QuoteBlock>
            )}
          </SectionWrapper>
        )}

        {sentimentData.objectionsOrPreferences &&
          sentimentData.objectionsOrPreferences.length > 0 && (
            <SectionWrapper>
              <MomentLabel>Objections / Preferences:</MomentLabel>
              {sentimentData.objectionsOrPreferences.map((item, index) => (
                <QuoteBlock key={index}>
                  <QuoteText>"{item.quote}"</QuoteText>
                  {item.timestamp && (
                    <TimestampButton onClick={() => handleTimestampClick(item.timestamp)}>
                      {item.timestamp}
                    </TimestampButton>
                  )}
                </QuoteBlock>
              ))}
            </SectionWrapper>
          )}

        {sentimentData.salesReadiness && (
          <div>
            <MomentLabel>Sales Readiness:</MomentLabel>
            <ProfileCardBlue>
              <ProfileValueBold>{sentimentData.salesReadiness.status}</ProfileValueBold>
              {sentimentData.salesReadiness.evidence && (
                <QuoteBlockWhite>
                  <QuoteText>"{sentimentData.salesReadiness.evidence.quote}"</QuoteText>
                  {sentimentData.salesReadiness.evidence.timestamp && (
                    <TimestampButton
                      onClick={() =>
                        handleTimestampClick(sentimentData.salesReadiness.evidence.timestamp)
                      }
                    >
                      {sentimentData.salesReadiness.evidence.timestamp}
                    </TimestampButton>
                  )}
                </QuoteBlockWhite>
              )}
            </ProfileCardBlue>
          </div>
        )}
      </SectionContent>
    </SectionContainer>
  );
};

ParentSentimentSection.propTypes = {
  sentimentData: PropTypes.object.isRequired,
  onSeek: PropTypes.func.isRequired,
  expanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
};

// Pitch Angle Section
const PitchAngleSection = ({ pitchAngle, onSeek, expanded, onToggle }) => (
  <SectionContainer>
    <SectionHeader onClick={onToggle}>
      <ToggleIcon expanded={expanded} />
      <SectionTitle>Your Pitch Angle</SectionTitle>
    </SectionHeader>
    <SectionContent expanded={expanded}>
      <ProfileCardYellow>
        <ProfileValuePitch>{renderTextWithTimestamps(pitchAngle, onSeek)}</ProfileValuePitch>
      </ProfileCardYellow>
    </SectionContent>
  </SectionContainer>
);

PitchAngleSection.propTypes = {
  pitchAngle: PropTypes.string.isRequired,
  onSeek: PropTypes.func.isRequired,
  expanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
};

// Next Actions Section
const NextActionsSection = ({ actions, onSeek, expanded, onToggle }) => {
  const handleTimestampClick = useCallback(
    timestamp => {
      onSeek(toSeconds(timestamp));
    },
    [onSeek],
  );

  return (
    <SectionContainer>
      <SectionHeader onClick={onToggle}>
        <ToggleIcon expanded={expanded} />
        <SectionTitle>Immediate Next Actions</SectionTitle>
      </SectionHeader>
      <SectionContent expanded={expanded}>
        {actions.map((action, index) => (
          <ActionCard key={index}>
            <PriorityWrapper>
              <PriorityBadge priority={action.priority}>Priority {action.priority}</PriorityBadge>
              {action.timestamp && (
                <TimestampButton onClick={() => handleTimestampClick(action.timestamp)}>
                  {action.timestamp}
                </TimestampButton>
              )}
            </PriorityWrapper>
            <ProfileValueBold>{action.action}</ProfileValueBold>
            {action.context && (
              <ProfileValueContext>
                {renderTextWithTimestamps(action.context, onSeek)}
              </ProfileValueContext>
            )}
          </ActionCard>
        ))}
      </SectionContent>
    </SectionContainer>
  );
};

NextActionsSection.propTypes = {
  actions: PropTypes.array.isRequired,
  onSeek: PropTypes.func.isRequired,
  expanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
};

// Ready to Send Messages Section
const ReadyToSendSection = ({ messages, onSeek, expanded, onToggle }) => (
  <SectionContainer>
    <SectionHeader onClick={onToggle}>
      <ToggleIcon expanded={expanded} />
      <SectionTitle>Ready to Send Messages</SectionTitle>
    </SectionHeader>
    <SectionContent expanded={expanded}>
      {messages.map((message, index) => (
        <MessageCard key={index}>
          <BadgesWrapper>
            <ChannelBadge>{message.channel}</ChannelBadge>
            <ToneText>{message.tone}</ToneText>
          </BadgesWrapper>
          <ProfileValuePitch>{renderTextWithTimestamps(message.message, onSeek)}</ProfileValuePitch>
        </MessageCard>
      ))}
    </SectionContent>
  </SectionContainer>
);

ReadyToSendSection.propTypes = {
  messages: PropTypes.array.isRequired,
  onSeek: PropTypes.func.isRequired,
  expanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
};

// Main Sales Pitch Panel Component
function SalesPitchPanel({ salesPitchData, videoTimeSec, onSeek, classroomId, demoDate }) {
  const [expandedSections, setExpandedSections] = useState({
    sessionPerformance: false,
    parentSentiment: false,
    pitchAngle: false,
    nextActions: false,
    readyToSend: false,
  });

  const toggleSection = useCallback(sectionName => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionName]: !prev[sectionName],
    }));
  }, []);

  // If no data, return null - parent component handles empty state
  if (!salesPitchData) {
    return null;
  }

  const handleExport = () => {
    const strip = text => (text ? text.replace(/\*\*(.*?)\*\*/g, '$1') : '');
    const lines = [];

    if (salesPitchData.studentProfile) {
      const p = salesPitchData.studentProfile;
      lines.push('=== STUDENT PROFILE ===');
      if (p.name) lines.push(`Name: ${p.name}`);
      if (p.grade) lines.push(`Grade: ${p.grade}`);
      if (p.tutor) lines.push(`Tutor: ${p.tutor}`);
      if (p.parents?.length)
        lines.push(`Parents: ${p.parents.map(par => `${par.name} (${par.relation})`).join(', ')}`);
      if (p.sessionGoal) lines.push(`Session Goal: ${p.sessionGoal}`);
      lines.push('');
    }

    if (salesPitchData.sessionPerformance) {
      const sp = salesPitchData.sessionPerformance;
      lines.push('=== SESSION PERFORMANCE ===');
      if (sp.whatClicked?.length) {
        lines.push('\nWHAT CLICKED:');
        sp.whatClicked.forEach(m => {
          lines.push(`${m.timestamp || ''} ${strip(m.moment)}`.trim());
          if (m.salesImplication) lines.push(`  Sales Implication: ${strip(m.salesImplication)}`);
        });
      }
      if (sp.whatStruggled?.length) {
        lines.push('\nWHAT THEY STRUGGLED WITH:');
        sp.whatStruggled.forEach(m => {
          lines.push(`${m.timestamp || ''} ${strip(m.moment)}`.trim());
          if (m.emotionalImpact) lines.push(`  Emotional Impact: ${strip(m.emotionalImpact)}`);
        });
      }
      lines.push('');
    }

    if (salesPitchData.parentSentiment) {
      const ps = salesPitchData.parentSentiment;
      lines.push('=== PARENT SENTIMENT ===');
      if (ps.decisionMaker) {
        lines.push(`Decision Maker: ${ps.decisionMaker.name}`);
        if (ps.decisionMaker.primaryConcern)
          lines.push(`Primary Concern: ${ps.decisionMaker.primaryConcern}`);
      }
      if (ps.urgency) {
        lines.push(`Urgency: ${ps.urgency.level}`);
        if (ps.urgency.evidence)
          lines.push(`  "${ps.urgency.evidence.quote}" ${ps.urgency.evidence.timestamp || ''}`);
      }
      if (ps.objectionsOrPreferences?.length) {
        lines.push('Objections/Preferences:');
        ps.objectionsOrPreferences.forEach(item =>
          lines.push(`  "${item.quote}" ${item.timestamp || ''}`),
        );
      }
      if (ps.salesReadiness) {
        lines.push(`Sales Readiness: ${ps.salesReadiness.status}`);
        if (ps.salesReadiness.evidence)
          lines.push(
            `  "${ps.salesReadiness.evidence.quote}" ${ps.salesReadiness.evidence.timestamp || ''}`,
          );
      }
      lines.push('');
    }

    if (salesPitchData.pitchAngle) {
      lines.push('=== YOUR PITCH ANGLE ===');
      lines.push(strip(salesPitchData.pitchAngle));
      lines.push('');
    }

    if (salesPitchData.nextActions?.length) {
      lines.push('=== NEXT ACTIONS ===');
      salesPitchData.nextActions.forEach(action => {
        lines.push(`Priority ${action.priority}: ${action.action}`);
        if (action.context) lines.push(`  ${strip(action.context)}`);
      });
      lines.push('');
    }

    if (salesPitchData.readyToSendMessages?.length) {
      lines.push('=== READY TO SEND MESSAGES ===');
      salesPitchData.readyToSendMessages.forEach(msg => {
        lines.push(`[${msg.channel}] (${msg.tone})`);
        lines.push(strip(msg.message));
        lines.push('');
      });
    }

    const date = demoDate || new Date().toISOString().split('T')[0];
    downloadTextFile(`salesPitch-${classroomId}-${date}.txt`, lines.join('\n'));
  };

  return (
    <PanelContainer>
      <ExportHeader>
        <ExportButton onClick={handleExport} aria-label="Export sales pitch">&#8595; Export</ExportButton>
      </ExportHeader>
      {salesPitchData.studentProfile && (
        <StudentProfileSection profileData={salesPitchData.studentProfile} />
      )}

      {salesPitchData.sessionPerformance && (
        <SessionPerformanceSection
          performanceData={salesPitchData.sessionPerformance}
          onSeek={onSeek}
          expanded={expandedSections.sessionPerformance}
          onToggle={() => toggleSection('sessionPerformance')}
        />
      )}

      {salesPitchData.parentSentiment && (
        <ParentSentimentSection
          sentimentData={salesPitchData.parentSentiment}
          onSeek={onSeek}
          expanded={expandedSections.parentSentiment}
          onToggle={() => toggleSection('parentSentiment')}
        />
      )}

      {salesPitchData.pitchAngle && (
        <PitchAngleSection
          pitchAngle={salesPitchData.pitchAngle}
          onSeek={onSeek}
          expanded={expandedSections.pitchAngle}
          onToggle={() => toggleSection('pitchAngle')}
        />
      )}

      {salesPitchData.nextActions && salesPitchData.nextActions.length > 0 && (
        <NextActionsSection
          actions={salesPitchData.nextActions}
          onSeek={onSeek}
          expanded={expandedSections.nextActions}
          onToggle={() => toggleSection('nextActions')}
        />
      )}

      {salesPitchData.readyToSendMessages && salesPitchData.readyToSendMessages.length > 0 && (
        <ReadyToSendSection
          messages={salesPitchData.readyToSendMessages}
          onSeek={onSeek}
          expanded={expandedSections.readyToSend}
          onToggle={() => toggleSection('readyToSend')}
        />
      )}
    </PanelContainer>
  );
}

SalesPitchPanel.propTypes = {
  salesPitchData: PropTypes.object,
  videoTimeSec: PropTypes.number.isRequired,
  onSeek: PropTypes.func.isRequired,
  classroomId: PropTypes.string,
  demoDate: PropTypes.string,
};

SalesPitchPanel.defaultProps = {
  salesPitchData: null,
  classroomId: '',
  demoDate: '',
};

export default SalesPitchPanel;
